import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Wallet, TrendingUp, Banknote, CreditCard, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { api, apiErr } from "@/lib/api";

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

function StatCard({ icon: Icon, label, value, gold = false, testid }) {
  return (
    <div data-testid={testid} className={`rounded-2xl border p-5 ${gold ? "border-[color:rgba(212,175,55,0.32)] bg-[color:var(--rb-obsidian)] shadow-[0_0_0_1px_rgba(212,175,55,0.10)]" : "border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)]"}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-[color:var(--rb-text-faint)]">
        <Icon className={`h-4 w-4 ${gold ? "text-[color:var(--rb-gold)]" : "text-[color:var(--rb-accent)]"}`} /> {label}
      </div>
      <div className={`rb-display mt-2 text-3xl font-semibold ${gold ? "text-[color:var(--rb-gold)]" : "text-[color:var(--rb-text)]"}`}>{value}</div>
    </div>
  );
}

export default function Earnings() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/earnings/summary");
      setData(res.data);
    } catch (e) {
      toast.error("Failed to load earnings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (params.get("connect") === "done") {
      toast.success("Stripe onboarding updated");
      params.delete("connect");
      setParams(params, { replace: true });
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onboard = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/connect/onboard", { origin_url: window.location.origin });
      window.location.href = d.url;
    } catch (e) {
      toast.error(apiErr(e, "Could not start onboarding"));
      setBusy(false);
    }
  };

  const cashout = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/earnings/cashout");
      toast.success(`Cashed out ${money(d.amount_cents)}`);
      load();
    } catch (e) {
      toast.error(apiErr(e, "Cash out failed"));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <AppShell><div className="flex justify-center py-24 text-[color:var(--rb-text-faint)]"><Loader2 className="h-6 w-6 animate-spin" /></div></AppShell>;
  }

  const connect = data?.connect || {};
  const balance = data?.available_balance_cents || 0;

  return (
    <AppShell>
      <div className="mb-6">
        <h1 className="rb-display text-2xl font-semibold tracking-tight text-[color:var(--rb-text)]">Earnings</h1>
        <p className="mt-1 text-sm text-[color:var(--rb-text-faint)]">You keep 80% of every marketplace sale, paid in credits. Only credits earned from cash purchases are cashable.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={TrendingUp} label="Total earned" value={money(data?.total_earned_cents)} testid="earnings-total" />
        <StatCard icon={Wallet} label="Available balance" value={money(balance)} gold testid="earnings-available" />
        <StatCard icon={Banknote} label="Paid out" value={money(data?.total_paid_out_cents)} testid="earnings-paidout" />
        <StatCard icon={CreditCard} label="Sales" value={data?.sales_count || 0} testid="earnings-sales-count" />
      </div>

      {/* Payout / Connect */}
      <div className="mt-6 rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-5">
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="rb-display text-lg font-semibold text-[color:var(--rb-text)]">Payouts</h2>
              {connect.onboarded ? (
                <Badge className="gap-1 bg-[color:rgba(45,212,191,0.14)] text-[color:var(--rb-accent)]"><CheckCircle2 className="h-3 w-3" /> Connected</Badge>
              ) : (
                <Badge variant="outline" className="border-[color:var(--rb-border)] text-[color:var(--rb-text-faint)]">Not connected</Badge>
              )}
            </div>
            <p className="mt-1 text-sm text-[color:var(--rb-text-faint)]">
              {connect.onboarded
                ? "Your Stripe Express account is ready to receive payouts."
                : "Connect a Stripe Express account to cash out your balance."}
            </p>
          </div>
          {connect.onboarded ? (
            <Button onClick={cashout} disabled={busy || balance <= 0} data-testid="cashout-button"
              className="rounded-xl bg-[color:var(--rb-accent)] text-[color:var(--rb-void)] hover:bg-[color:var(--rb-accent-2)]">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Cash out ${money(balance)}`}
            </Button>
          ) : (
            <Button onClick={onboard} disabled={busy} data-testid="connect-onboard-button"
              className="rounded-xl bg-[color:var(--rb-accent)] text-[color:var(--rb-void)] hover:bg-[color:var(--rb-accent-2)]">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Set up payouts"}
            </Button>
          )}
        </div>
      </div>

      {/* Chart */}
      {data?.chart?.length > 0 && (
        <div className="mt-6 rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-5">
          <h2 className="rb-display mb-4 text-lg font-semibold text-[color:var(--rb-text)]">Earnings over time</h2>
          <div className="h-56 w-full" style={{ minHeight: 224 }}>
            <ResponsiveContainer width="100%" height={224} minWidth={0} debounce={50}>
              <AreaChart data={data.chart} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                <defs>
                  <linearGradient id="rbarea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2DD4BF" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#2DD4BF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(94,234,212,0.08)" />
                <XAxis dataKey="date" stroke="rgba(236,253,245,0.5)" fontSize={11} />
                <YAxis stroke="rgba(236,253,245,0.5)" fontSize={11} />
                <Tooltip contentStyle={{ background: "#0A1B19", border: "1px solid rgba(94,234,212,0.2)", borderRadius: 12, color: "#ECFDF5" }} />
                <Area type="monotone" dataKey="amount" stroke="#2DD4BF" strokeWidth={2} fill="url(#rbarea)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Sales history */}
      <div className="mt-6 rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-5">
        <h2 className="rb-display mb-4 text-lg font-semibold text-[color:var(--rb-text)]">Sales history</h2>
        {(!data?.sales || data.sales.length === 0) ? (
          <p className="py-6 text-sm text-[color:var(--rb-text-faint)]">No sales yet. Publish skills to the marketplace to start earning.</p>
        ) : (
          <div className="overflow-x-auto" data-testid="sales-history-table">
            <Table>
              <TableHeader>
                <TableRow className="border-[color:var(--rb-border)]">
                  <TableHead className="text-[color:var(--rb-text-faint)]">Skill</TableHead>
                  <TableHead className="text-[color:var(--rb-text-faint)]">Buyer</TableHead>
                  <TableHead className="text-[color:var(--rb-text-faint)]">Amount</TableHead>
                  <TableHead className="text-[color:var(--rb-text-faint)]">Your cut</TableHead>
                  <TableHead className="text-[color:var(--rb-text-faint)]">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.sales.map((s, i) => (
                  <TableRow key={`${s.skill_id}-${s.created_at}-${i}`} className="border-[color:var(--rb-border)]">
                    <TableCell className="rb-mono text-[color:var(--rb-text)]">{s.skill_name}</TableCell>
                    <TableCell className="text-[color:var(--rb-text-dim)]">{s.is_own ? "You (self)" : s.buyer_name}</TableCell>
                    <TableCell className="text-[color:var(--rb-text-dim)]">{money(s.amount_cents)}</TableCell>
                    <TableCell className="text-[color:var(--rb-gold)]">{money(s.payout_cents)}</TableCell>
                    <TableCell className="text-[color:var(--rb-text-faint)]">{(s.created_at || "").slice(0, 10)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </AppShell>
  );
}
